import Cookies from 'js-cookie';
import { isStaffAnalyticsSession } from './staffAnalytics';

declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

const COOKIE_NAME = 'tt_cookie_consent';
const COOKIE_MAX_AGE_DAYS = 365;
const COOKIE_DOMAIN =
  window.location.hostname === 'tokentax.co' || window.location.hostname.endsWith('.tokentax.co')
    ? '.tokentax.co'
    : undefined;

export type ConsentValue = 'accepted' | 'rejected';

export function getConsentCookie(): ConsentValue | null {
  const value = Cookies.get(COOKIE_NAME);
  if (value === 'accepted' || value === 'rejected') return value;
  return null;
}

export function setConsentCookie(value: ConsentValue) {
  Cookies.set(COOKIE_NAME, value, { expires: COOKIE_MAX_AGE_DAYS, path: '/', domain: COOKIE_DOMAIN });
}

export function hasGlobalPrivacyControl() {
  return navigator.globalPrivacyControl === true;
}

// Keep in sync with the consent defaults in index.html, the marketing site and the API.
export function isMarketingAllowed(value: ConsentValue | null = getConsentCookie()) {
  return value !== 'rejected' && !hasGlobalPrivacyControl();
}

export function applyRudderStackConsent(value: ConsentValue) {
  const allowed = isMarketingAllowed(value);
  // Record an explicit choice separately from the SDK's default Marketing
  // flag, so warehouse imports can honor it.
  window.rudderanalytics?.consent?.({
    // Still apply the browser's privacy choice, but never emit it as consent
    // for a customer that staff are viewing (or previously viewed in this tab).
    trackConsent: !isStaffAnalyticsSession(),
    consentManagement: {
      allowedConsentIds: allowed ? ['Marketing'] : [],
      deniedConsentIds: allowed ? [] : ['Marketing'],
    },
  });
}

export function applyGtagConsent(value: ConsentValue) {
  const granted = isMarketingAllowed(value) ? 'granted' : 'denied';
  window.gtag?.('consent', 'update', {
    ad_storage: granted,
    ad_user_data: granted,
    ad_personalization: granted,
    analytics_storage: granted,
  });
}

// Lightweight pub/sub so other components can reopen the banner
type Listener = () => void;
const listeners: Listener[] = [];

export function onShowConsentBanner(fn: Listener) {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

export function showConsentBanner() {
  listeners.forEach((fn) => fn());
}
